import { Component, OnInit } from "@angular/core";
import { MessageService } from "src/services/message/message.service";
import { ActivatedRoute } from "@angular/router";
import { PaymentsService } from "src/services/payment/payments.service";
import { LoadingService } from "src/services/loading/loading.service";
import { User } from "src/services/user/user.interface";
import { AuthService } from "src/services/auth/auth.service";
import { InAppBrowser } from "@awesome-cordova-plugins/in-app-browser/ngx";
import { PendingOrderApiService } from "src/services/pending-order/pending-order-api.service";
import { PendingOrder } from "src/services/pending-order/pending-order.interface";
import { OrderPromotionService } from "src/services/order/order-promotion.service";
import { NavigationService } from "src/services/navigation/navigation.service";
import { isAnyPickupType } from "src/services/subscription/plan-service-type.enum";
import { BackendBody, OrderFees } from "../../../../shared/backend-api.types";
import { OrderFeesService } from "../../../../services/order-fees/order-fees.service";

export const deliveryIdParam = "check-in";
export const pickupIdParam = "check-out";

export enum PaymentRequirement {
  free,
  required,
  deferred,
}

export type OrderInfo = {
  displayReason?: string;
  fees?: OrderFees;
  order?: PendingOrder;
  paymentRequirements: PaymentRequirement;
  price?: number;
};

@Component({
  templateUrl: "./pay-for-delivery.page.html",
  styleUrls: ["./pay-for-delivery.page.scss"],
  standalone: false,
})
export class PayForDeliveryPage implements OnInit {
  public activeUser: User;
  public handlingPaymentOfDelivery: boolean;
  public handlingPaymentOfPickup: boolean;
  public deliveryOrderInfo: OrderInfo = { paymentRequirements: PaymentRequirement.free };
  public cost: number = 0;
  public fees: OrderFees;
  public loading = true;
  public pickupOrderInfo: OrderInfo = { paymentRequirements: PaymentRequirement.free };
  public pendingDeliveryOrderId: string | null;
  public pendingPickupOrderId: string | null;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly iab: InAppBrowser,
    private readonly loadingService: LoadingService,
    private readonly messageService: MessageService,
    private readonly navigationService: NavigationService,
    private readonly orderFeesService: OrderFeesService,
    private readonly orderPromotionService: OrderPromotionService,
    private readonly paymentsService: PaymentsService,
    private readonly pendingOrderService: PendingOrderApiService,
  ) {}

  public async ngOnInit(): Promise<void> {
    const activeUser = await this.authService.getActiveUser();
    if (!activeUser) {
      return;
    }

    this.activeUser = activeUser;
    this.pendingDeliveryOrderId = this.activatedRoute.snapshot.queryParamMap.get(deliveryIdParam);
    this.pendingPickupOrderId = this.activatedRoute.snapshot.queryParamMap.get(pickupIdParam);

    this.handlingPaymentOfDelivery = !!this.pendingDeliveryOrderId;
    this.handlingPaymentOfPickup = !!this.pendingPickupOrderId;

    this.fees =
      (await this.orderFeesService.getDefaultOrderFeesForUser(this.activeUser.id)) ??
      (await this.orderFeesService.getDefaultOrderFees());

    try {
      await this.loadingService.wrapInLoading(async () => {
        if (this.pendingDeliveryOrderId) {
          await this.computePaymentExpectationsForOrderId(
            this.pendingDeliveryOrderId,
            this.deliveryOrderInfo,
            this.fees,
          );
        }

        if (this.pendingPickupOrderId) {
          await this.computePaymentExpectationsForOrderId(this.pendingPickupOrderId, this.pickupOrderInfo, this.fees);
        }
      });
    } finally {
      this.loading = false;
    }
  }

  public async pay(): Promise<void> {
    const pickupOrderId = this.pendingPickupOrderId;
    if (!pickupOrderId) {
      await this.presentPaymentIssueToast();
      return;
    }

    try {
      const session = await this.paymentsService.createStripeCheckoutSessionForOrder(
        this.activeUser.id,
        pickupOrderId,
        this.pendingDeliveryOrderId ?? undefined,
      );

      const browser = this.iab.create(session.url, "_self", { beforeload: "get" });
      browser.show();
    } catch {
      await this.presentPaymentIssueToast();
    }
  }

  public async promote(): Promise<void> {
    if (this.cost === 0) {
      if (this.pendingDeliveryOrderId) {
        await this.orderPromotionService.promoteOrder(this.pendingDeliveryOrderId, this.activeUser?.id);
      }

      if (this.pendingPickupOrderId) {
        await this.orderPromotionService.promoteOrder(this.pendingPickupOrderId, this.activeUser?.id);
      }

      await this.navigationService.goToDeliveriesPage();
    }
  }

  private async computePaymentExpectationsForOrderId(
    orderId: string,
    orderInfoToUpdate: OrderInfo,
    fees: OrderFees,
  ): Promise<void> {
    const order = await this.pendingOrderService.getById(orderId);

    orderInfoToUpdate.order = order;
    orderInfoToUpdate.fees = fees;
    const deliveryPaymentRequirement = await this.getPaymentRequirements(order);

    if (deliveryPaymentRequirement.paymentRequired) {
      this.cost += deliveryPaymentRequirement.price;
      orderInfoToUpdate.paymentRequirements = PaymentRequirement.required;
      orderInfoToUpdate.price = deliveryPaymentRequirement.price;
    } else {
      orderInfoToUpdate.paymentRequirements =
        deliveryPaymentRequirement.reason === "nationwideAlwaysRequirePayment"
          ? PaymentRequirement.deferred
          : PaymentRequirement.free;
    }

    orderInfoToUpdate.displayReason = await this.paymentsService.getPaymentRequiredReasonDisplayString(
      deliveryPaymentRequirement.reason,
      fees,
    );
  }

  private getPaymentRequirements(order: PendingOrder): Promise<BackendBody["PaymentRequirement"]> {
    if (isAnyPickupType(order.serviceType) && (!order.items || order.items.length === 0)) {
      return this.paymentsService.checkIfPaymentRequiredForPickupOnly(order.serviceType);
    } else {
      return this.paymentsService.checkIfPaymentRequired(order.items ?? [], order.serviceType);
    }
  }

  private async presentPaymentIssueToast(): Promise<void> {
    await this.messageService.presentErrorToast(
      "There was an issue processing your payment. Your order has been saved and our team will follow up shortly.",
    );
  }
}
